/**
 * OAuth Callback page for Auto Music application.
 *
 * This component:
 * - Receives the redirect from backend after OAuth completion
 * - Extracts JWT token from URL parameters
 * - Stores token in localStorage
 * - Redirects to dashboard/home page
 * - Handles errors during the callback process
 */

import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

export default function Callback() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState<'processing' | 'success' | 'error'>('processing');
  const [errorMessage, setErrorMessage] = useState<string>('');

  useEffect(() => {
    const processCallback = async () => {
      try {
        // Extract token and user_id from URL parameters
        const token = searchParams.get('token');
        const userId = searchParams.get('user_id');
        const error = searchParams.get('error');

        // Check for errors
        if (error) {
          setStatus('error');
          setErrorMessage(
            error === 'access_denied'
              ? 'You denied access to your Spotify account.'
              : `Authentication error: ${error}`
          );
          // Redirect to login after 3 seconds
          setTimeout(() => navigate('/login'), 3000);
          return;
        }

        // Validate token presence
        if (!token) {
          setStatus('error');
          setErrorMessage('No authentication token received. Please try again.');
          setTimeout(() => navigate('/login'), 3000);
          return;
        }

        // Store token in localStorage
        localStorage.setItem('auth_token', token);

        // Optionally store user_id
        if (userId) {
          localStorage.setItem('user_id', userId);
        }

        // Set success status
        setStatus('success');

        // Fetch user info to verify token works
        try {
          const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000';
          const response = await fetch(`${API_BASE_URL}/api/auth/me`, {
            headers: {
              'Authorization': `Bearer ${token}`,
            },
          });

          if (response.ok) {
            const userData = await response.json();
            // Store user data
            localStorage.setItem('user_data', JSON.stringify(userData));
          }
        } catch (err) {
          console.error('Failed to fetch user data:', err);
          // Non-critical error, continue with redirect
        }

        // Redirect to dashboard after short delay
        setTimeout(() => navigate('/'), 1500);
      } catch (err) {
        console.error('Callback processing error:', err);
        setStatus('error');
        setErrorMessage('An unexpected error occurred. Please try logging in again.');
        setTimeout(() => navigate('/login'), 3000);
      }
    };

    processCallback();
  }, [searchParams, navigate]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-900 via-blue-900 to-black flex items-center justify-center px-4">
      <div className="max-w-md w-full">
        <div className="bg-white/10 backdrop-blur-lg rounded-2xl shadow-2xl p-8 border border-white/20">
          {status === 'processing' && (
            <div className="text-center space-y-4">
              {/* Loading Spinner */}
              <div className="flex justify-center">
                <svg
                  className="animate-spin h-16 w-16 text-green-500"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  ></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>
              </div>
              <h2 className="text-2xl font-semibold text-white">
                Completing Login...
              </h2>
              <p className="text-gray-300">
                Please wait while we set up your account
              </p>
            </div>
          )}

          {status === 'success' && (
            <div className="text-center space-y-4">
              {/* Success Icon */}
              <div className="flex justify-center">
                <div className="rounded-full bg-green-500/20 p-3">
                  <svg
                    className="w-16 h-16 text-green-500"
                    fill="none"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path d="M5 13l4 4L19 7"></path>
                  </svg>
                </div>
              </div>
              <h2 className="text-2xl font-semibold text-white">
                Login Successful!
              </h2>
              <p className="text-gray-300">
                Redirecting to your dashboard...
              </p>
            </div>
          )}

          {status === 'error' && (
            <div className="text-center space-y-4">
              {/* Error Icon */}
              <div className="flex justify-center">
                <div className="rounded-full bg-red-500/20 p-3">
                  <svg
                    className="w-16 h-16 text-red-500"
                    fill="none"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path d="M6 18L18 6M6 6l12 12"></path>
                  </svg>
                </div>
              </div>
              <h2 className="text-2xl font-semibold text-white">
                Authentication Failed
              </h2>
              <p className="text-gray-300">{errorMessage}</p>
              <p className="text-sm text-gray-400">
                Redirecting to login page...
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
